import { Dialog } from '../../../components/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import ContentUnavailable from '../../../components/common/ContentUnavailable';
import moment from 'moment-timezone';
import { toast } from '../../../components/ui/notify';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { isNotFoundError } from '../../../utils/isNotFoundError';

import {
  finishAdaptiveClassroomAssessment,
  getAdaptiveClassroomNextQuestion,
  getAllQuestionsForClassroomAssessmentForUser,
  getUserClassroomAssessmentById,
  saveIndividualQuestionAnswer,
  startAdaptiveClassroomAssessment,
  submitAdaptiveClassroomAnswer,
  submitClassroomAssessment,
} from '../../../services/user/classroom.services';
import { useTimezone } from '../../../context/TimezoneContext';
import useClassroomRealtime from '../../../hooks/useClassroomRealtime';
import { CLASSROOM_REALTIME_EVENTS } from '../../../services/classroomRealtime.service';

function AssessmentQuiz() {
  const navigate = useNavigate();
  const { encodedAssessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const encodedClassroomId = searchParams.get('classroomid');

  const assessment_id = decodeData(decodeURIComponent(encodedAssessmentId));
  const classroom_id = encodedClassroomId ? decodeData(decodeURIComponent(encodedClassroomId)) : null;
  const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;
  const { timeZone: userTimeZone } = useTimezone();

  const [assessmentData, setAssessmentData] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadStatus, setLoadStatus] = useState('loading');
  const [quizStarted, setQuizStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [progress, setProgress] = useState(0);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState({});
  const [savedAnswers, setSavedAnswers] = useState({});

  const [adaptiveAttempt, setAdaptiveAttempt] = useState(null);
  const [adaptiveQuestion, setAdaptiveQuestion] = useState(null);
  const [adaptiveQuestionNumber, setAdaptiveQuestionNumber] = useState(1);
  const [adaptiveQuestionStartedAt, setAdaptiveQuestionStartedAt] = useState(null);

  const answersRef = useRef({});
  const isSubmittingRef = useRef(false);
  const assessmentFinishedRef = useRef(false);
  const submissionResultRef = useRef(null);
  const adaptiveAttemptIdRef = useRef(null);
  const isAdaptiveRef = useRef(false);
  const assessmentQuestionsRef = useRef([]);
  const initSessionRef = useRef(null);
  const mountedRef = useRef(true);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const unavailableRef = useRef(false);

  const classroomDetailsPath = classroom_id
    ? `/user/AllClassroom/classroomDetails/${encodeURIComponent(encodeData(classroom_id))}?active-tab=assessment`
    : '/user/AllClassroom';

  const markUnavailable = useCallback(() => {
    unavailableRef.current = true;
    setQuizStarted(false);
    setShowExitConfirm(false);
    setLoadStatus('unavailable');
  }, []);

  useClassroomRealtime(classroom_id, {
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_DEACTIVATED]: (payload) => {
      if (Number(payload.assessmentId ?? payload.assessment_id) === Number(assessment_id)) markUnavailable();
    },
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_UPDATED]: (payload) => {
      if (Number(payload.assessmentId ?? payload.assessment_id) === Number(assessment_id) &&
          (Number(payload.is_active) === 0 || Number(payload.is_visible) === 0)) markUnavailable();
    },
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_VISIBILITY_CHANGED]: (payload) => {
      if (Number(payload.assessmentId ?? payload.assessment_id) === Number(assessment_id) &&
          Number(payload.is_visible) === 0) markUnavailable();
    },
  });

  useEffect(() => {
    if (loadStatus !== 'available' || submissionResult || !assessment_id || !classroom_id) return undefined;

    const checkAvailability = async () => {
      try {
        const response = await getUserClassroomAssessmentById({ assessment_id, class_id: classroom_id });
        if (!response?.success && (response?.statusCode === 404 || isNotFoundError(response))) {
          markUnavailable();
        }
      } catch (error) {
        if (isNotFoundError(error)) markUnavailable();
      }
    };

    const onFocus = () => checkAvailability();
    const onVisibilityChange = () => {
      if (!document.hidden) checkAvailability();
    };
    const timer = window.setInterval(checkAvailability, 15000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [assessment_id, classroom_id, loadStatus, submissionResult, markUnavailable]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    submissionResultRef.current = submissionResult;
  }, [submissionResult]);

  useEffect(() => {
    adaptiveAttemptIdRef.current = adaptiveAttempt?.attempt_id ?? null;
  }, [adaptiveAttempt?.attempt_id]);

  const isAdaptive = Number(assessmentData?.is_adaptive) === 1;

  useEffect(() => {
    isAdaptiveRef.current = isAdaptive;
  }, [isAdaptive]);

  useEffect(() => {
    assessmentQuestionsRef.current = assessmentData?.questions || [];
  }, [assessmentData?.questions]);

  const isAssessmentActive = Boolean(
    quizStarted &&
    loadStatus === 'available' &&
    !submissionResult &&
    !isLoading &&
    assessmentData &&
    (isAdaptive ? adaptiveAttempt?.attempt_id : assessmentData?.questions?.length)
  );

  useEffect(() => {
    if (!isAssessmentActive) return undefined;

    window.history.pushState({ assessmentQuizGuard: true }, '');
    const onPopState = () => {
      window.history.pushState({ assessmentQuizGuard: true }, '');
      setShowExitConfirm(true);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isAssessmentActive]);

  useEffect(() => {
    if (!isAssessmentActive) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isAssessmentActive]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      setTimeout(() => {
        if (mountedRef.current) {
          return;
        }

        if (unavailableRef.current || assessmentFinishedRef.current || submissionResultRef.current) {
          return;
        }

        if (isAdaptiveRef.current && adaptiveAttemptIdRef.current) {
          finishAdaptiveClassroomAssessment({
            attempt_id: adaptiveAttemptIdRef.current,
            reason: 'stopped',
          });
          return;
        }

        const savedAnswers = answersRef.current;
        if (savedAnswers && Object.keys(savedAnswers).length > 0 && classroom_id && assessment_id) {
          const answersArray = Object.entries(savedAnswers)
            .map(([questionIndex, optionId]) => ({
              question_id: assessmentQuestionsRef.current?.[Number(questionIndex)]?.id,
              option: String(optionId),
            }))
            .filter((entry) => entry.question_id);

          if (answersArray.length > 0) {
            submitClassroomAssessment({
              class_id: classroom_id,
              assessment_id,
              user_id: currentUserId,
              quiz_answers: answersArray,
            });
          }
        }
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncTimeLeftFromAttempt = (attempt) => {
    if (!attempt?.end_time) {
      setTimeLeft(null);
      return;
    }

    const diffSeconds = Math.max(
      0,
      Math.floor((new Date(attempt.end_time).getTime() - Date.now()) / 1000)
    );
    setTimeLeft(diffSeconds);
  };

  const updateAdaptiveProgress = (attempt, hasCurrentQuestion = false) => {
    const attempted = Number(attempt?.attempted_questions || attempt?.total_attempted || 0);
    const total = Number(attempt?.total_questions || 0);
    const visibleProgressCount = attempted + (hasCurrentQuestion ? 1 : 0);
    setAdaptiveQuestionNumber(Math.min(visibleProgressCount || 1, total || 1));
    setProgress(total > 0 ? (attempted / total) * 100 : 0);
  };

  const applyAdaptiveResponse = (payload, successMessage = null) => {
    const adaptiveData = payload?.data ?? payload;
    if (!adaptiveData) {
      toast.error('Adaptive assessment response was empty.');
      return;
    }

    if (adaptiveData.finished) {
      setAdaptiveQuestion(null);
      setAdaptiveAttempt(adaptiveData.attempt || null);
      setSubmissionResult(adaptiveData.attempt || null);
      setQuizStarted(false);
      setProgress(100);
      setTimeLeft(0);
      if (successMessage) {
        toast.success(successMessage);
      }
      return;
    }

    setAdaptiveAttempt(adaptiveData.attempt || null);
    setAdaptiveQuestion(adaptiveData.question || null);
    setAdaptiveQuestionStartedAt(Date.now());
    updateAdaptiveProgress(adaptiveData.attempt, Boolean(adaptiveData.question));
    syncTimeLeftFromAttempt(adaptiveData.attempt);
    setQuizStarted(true);
    if (successMessage) {
      toast.success(successMessage, { autoClose: 1800 });
    }
  };

  useEffect(() => {
    if (!assessment_id || !classroom_id) {
      return undefined;
    }

    const sessionId = Symbol('assessment-init');
    initSessionRef.current = sessionId;
    let cancelled = false;

    const isActiveSession = () => !cancelled && !unavailableRef.current && initSessionRef.current === sessionId;

    const initializeAssessment = async () => {
      setIsLoading(true);
      setLoadStatus('loading');
      try {
        const response = await getAllQuestionsForClassroomAssessmentForUser({
          assessment_id,
          class_id: classroom_id,
        });

        if (!isActiveSession()) {
          return;
        }

        if (!response?.success) {
          if (isNotFoundError({ message: response?.message }) || response?.statusCode === 404) {
            markUnavailable();
            return;
          }
          toast.error(response?.message || 'Unable to load assessment.');
          setLoadStatus('error');
          return;
        }

        const assessment = response.data?.assessment;
        setAssessmentData(assessment);
        setLoadStatus('available');

        if (Number(assessment?.is_adaptive) === 1) {
          const startResponse = await startAdaptiveClassroomAssessment({
            assessment_id,
            class_id: classroom_id,
          });

          if (!isActiveSession()) {
            return;
          }

          if (!startResponse?.success) {
            if (startResponse?.statusCode === 404 || isNotFoundError(startResponse)) markUnavailable();
            else toast.error(startResponse?.message || 'Unable to start adaptive assessment.');
            return;
          }

          const attempt = startResponse.data?.attempt;
          setAdaptiveAttempt(attempt || null);
          syncTimeLeftFromAttempt(attempt);

          const nextResponse = await getAdaptiveClassroomNextQuestion(attempt?.attempt_id);
          if (!isActiveSession()) {
            return;
          }

          if (!nextResponse?.success) {
            if (nextResponse?.statusCode === 404 || isNotFoundError(nextResponse)) markUnavailable();
            else toast.error(nextResponse?.message || 'Unable to load adaptive question.');
            return;
          }

          applyAdaptiveResponse(nextResponse);
        } else {
          if (assessment?.duration > 0) {
            setTimeLeft(assessment.duration * 60);
          } else {
            setTimeLeft(null);
          }
          setQuizStarted(true);
        }
      } catch (error) {
        if (!isActiveSession()) {
          return;
        }
        console.error('Error fetching assessment questions:', error);
        if (isNotFoundError(error)) {
          markUnavailable();
          return;
        }
        toast.error('Error loading assessment.');
        setLoadStatus('error');
      } finally {
        if (isActiveSession()) {
          setIsLoading(false);
        }
      }
    };

    initializeAssessment();

    return () => {
      cancelled = true;
      if (initSessionRef.current === sessionId) {
        initSessionRef.current = null;
      }
    };
  }, [assessment_id, classroom_id]);

  useEffect(() => {
    let timer;
    if (quizStarted && timeLeft !== null && timeLeft > 0 && !submissionResult) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          const newTime = prevTime - 1;

          if (newTime === 25) {
            toast.warning('Only 25 seconds remaining!', {
              toastId: 'timer-25',
              position: 'top-center',
              autoClose: 3000,
            });
          } else if (newTime === 10) {
            toast.error('Only 10 seconds remaining!', {
              toastId: 'timer-10',
              position: 'top-center',
              autoClose: 3000,
            });
          }

          if (newTime <= 0) {
            clearInterval(timer);
            if (isAdaptive && adaptiveAttempt?.attempt_id) {
              handleAdaptiveFinish('expired');
            } else {
              handleSubmit(true);
            }
            return 0;
          }

          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [quizStarted, submissionResult, isAdaptive, adaptiveAttempt]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatUtcToLocal = (value) => {
    if (!value) return 'N/A';
    const parsed = moment.utc(
      value,
      [
        moment.ISO_8601,
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
        'YYYY-MM-DDTHH:mm:ss[Z]',
      ],
      true
    );

    if (!parsed.isValid()) return 'N/A';
    const local = parsed.tz(userTimeZone);
    return `${local.format('MMM DD, YYYY hh:mm A')} (${local.format('z')})`;
  };

  const handleOptionSelect = (optionId) => {
    if (isAdaptive) {
      setSelectedAnswer(optionId);
      return;
    }

    setSelectedAnswer(optionId);
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [currentQuestionIndex]: optionId,
      };
      answersRef.current = newAnswers;
      return newAnswers;
    });

    const answeredQuestionsCount = Object.keys(answers).length + (answers[currentQuestionIndex] ? 0 : 1);
    const totalQuestions = Number(assessmentData?.total_questions || 0);
    setProgress(totalQuestions > 0 ? (answeredQuestionsCount / totalQuestions) * 100 : 0);
  };

  const moveToNextNormalQuestion = () => {
    if (currentQuestionIndex < (assessmentData?.questions?.length - 1)) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedAnswer(answers[currentQuestionIndex + 1] || null);
    }
  };

  const handleNormalNext = async () => {
    if (!selectedAnswer) {
      toast.warning('Please select an answer before proceeding.');
      return;
    }

    const currentQuestion = assessmentData.questions[currentQuestionIndex];
    if (savedAnswers[currentQuestionIndex]) {
      moveToNextNormalQuestion();
      return;
    }

    setIsSavingAnswer(true);
    try {
      const response = await saveIndividualQuestionAnswer({
        class_id: classroom_id,
        assessment_id,
        user_id: currentUserId,
        question_id: currentQuestion.id,
        option: selectedAnswer,
      });

      if (response?.success) {
        setSavedAnswers((prev) => ({
          ...prev,
          [currentQuestionIndex]: true,
        }));
        moveToNextNormalQuestion();
      } else {
        if (response?.statusCode === 404 || isNotFoundError(response)) markUnavailable();
        else toast.error('Failed to save answer. Please try again.');
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      if (isNotFoundError(error)) markUnavailable();
      else toast.error('Error saving answer. Please try again.');
    } finally {
      setIsSavingAnswer(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      setSelectedAnswer(answers[currentQuestionIndex - 1] || null);
    }
  };

  const handleSubmit = async (isAutoSubmit = false) => {
    if (isSubmittingRef.current || submissionResult) {
      return false;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const currentAnswers = isAutoSubmit ? answersRef.current : answers;
    const answersArray = Object.entries(currentAnswers).map(([questionIndex, optionId]) => ({
      question_id: assessmentData.questions[Number(questionIndex)].id,
      option: String(optionId),
    }));

    try {
      const response = await submitClassroomAssessment({
        class_id: classroom_id,
        assessment_id,
        user_id: currentUserId,
        quiz_answers: answersArray,
      });

      if (response?.success) {
        assessmentFinishedRef.current = true;
        setSubmissionResult(response.data);
        setQuizStarted(false);
        setProgress(100);
        if (!isAutoSubmit) {
          toast.success('Assessment submitted successfully!', {
            toastId: 'assessment-submitted',
          });
        }
        return true;
      }

      if (response?.statusCode === 404 || isNotFoundError(response)) markUnavailable();
      else toast.error('Failed to submit assessment. Please try again.');
      return false;
    } catch (error) {
      console.error('Error submitting assessment:', error);
      if (isNotFoundError(error)) markUnavailable();
      else toast.error('Error submitting assessment. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const handleAdaptiveNext = async () => {
    if (!selectedAnswer || !adaptiveAttempt?.attempt_id || !adaptiveQuestion?.id) {
      toast.warning('Please select an answer before proceeding.');
      return;
    }

    setIsSavingAnswer(true);
    try {
      const response = await submitAdaptiveClassroomAnswer({
        attempt_id: adaptiveAttempt.attempt_id,
        question_id: adaptiveQuestion.id,
        selected_answer: selectedAnswer,
        time_taken: adaptiveQuestionStartedAt
          ? Math.max(0, Math.floor((Date.now() - adaptiveQuestionStartedAt) / 1000))
          : 0,
      });

      if (response?.success) {
        setSelectedAnswer(null);
        applyAdaptiveResponse(response.data);
      } else {
        if (response?.statusCode === 404 || isNotFoundError(response)) markUnavailable();
        else toast.error(response?.message || 'Failed to submit answer. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting adaptive answer:', error);
      if (isNotFoundError(error)) markUnavailable();
      else toast.error('Error submitting answer. Please try again.');
    } finally {
      setIsSavingAnswer(false);
    }
  };

  const handleAdaptiveFinish = async (reason = 'stopped') => {
    if (!adaptiveAttempt?.attempt_id || isSubmittingRef.current) {
      return false;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await finishAdaptiveClassroomAssessment({
        attempt_id: adaptiveAttempt.attempt_id,
        reason,
      });

      if (response?.success) {
        assessmentFinishedRef.current = true;
        setAdaptiveQuestion(null);
        setSubmissionResult(response.data);
        setQuizStarted(false);
        setProgress(100);
        setTimeLeft(0);
        if (reason === 'expired') {
          toast.success('Time is up. Assessment finished.');
        }
        return true;
      }

      if (response?.statusCode === 404 || isNotFoundError(response)) markUnavailable();
      else toast.error(response?.message || 'Failed to finish adaptive assessment.');
      return false;
    } catch (error) {
      console.error('Error finishing adaptive assessment:', error);
      if (isNotFoundError(error)) markUnavailable();
      else toast.error('Error finishing adaptive assessment.');
      return false;
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const handleCancelExit = () => {
    setShowExitConfirm(false);
  };

  const handleConfirmStopAssessment = async () => {
    setShowExitConfirm(false);

    let finished = false;
    if (isAdaptive) {
      finished = await handleAdaptiveFinish('stopped');
    } else {
      finished = await handleSubmit(false);
    }

    if (!finished) {
      toast.error('Could not stop the assessment. Please try again.');
    }
  };

  const requestExitConfirmation = () => {
    if (!isAssessmentActive) {
      navigate(classroomDetailsPath);
      return;
    }
    setShowExitConfirm(true);
  };

  const renderExitConfirmModal = () => {
    if (!showExitConfirm) return null;

    return (
      <Dialog show onHide={handleCancelExit} keyboard={!(isSubmitting)} backdrop={(isSubmitting) ? 'static' : true}>
            <Dialog.Header closeButton={!(isSubmitting)}>
              <Dialog.Title >Stop assessment?</Dialog.Title>

            </Dialog.Header>
            <Dialog.Body >
              <p className="!tw-text-[var(--secondary-color)] tw-mb-0 tw-text-slate-500">
                If you leave now, your assessment will be stopped and your current progress will be submitted.
                You will see your result on the next screen.
              </p>
            </Dialog.Body>
            <Dialog.Footer >
              <button
                type="button"
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-outline-secondary"}
                onClick={handleCancelExit}
                disabled={isSubmitting}
              >
                No, continue quiz
              </button>
              <button
                type="button"
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                onClick={handleConfirmStopAssessment}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Stopping...' : 'Yes, stop assessment'}
              </button>
            </Dialog.Footer>
          </Dialog>
    );
  };

  if (submissionResult) {
    const attemptedCount = Number(
      submissionResult.attempted_questions ?? submissionResult.total_attempted ?? 0
    );
    const correctCount = Number(
      submissionResult.correct_answers ?? submissionResult.total_correct ?? 0
    );
    const wrongCount = Number(
      submissionResult.wrong_answers ?? Math.max(attemptedCount - correctCount, 0)
    );
    const unansweredCount = Number(
      submissionResult.unanswered_questions ??
        Math.max(Number(submissionResult.total_questions || 0) - attemptedCount, 0)
    );
    const percentage = Number(submissionResult.percentage || 0);
    const passPercentage = Number(submissionResult.pass_percentage || 0);
    const passed =
      submissionResult.pass_status
        ? submissionResult.pass_status === 'pass'
        : percentage >= passPercentage;

    return (
      <>
        {renderExitConfirmModal()}
        <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container tw-py-12">
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
          <div className="tw-min-w-[0px] ui-column lg:tw-w-2/3">
            <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-shadow-sm tw-border-0"}>
              <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body tw-p-12">
                <div className="tw-text-center tw-mb-6">
                  <div className="tw-bg-amber-600 tw-bg-opacity-10 tw-rounded-full tw-inline-flex tw-items-center tw-justify-center tw-mb-4" style={{ width: '80px', height: '80px' }}>
                    <Icon icon="mdi:information" className="!tw-text-[var(--warning-color)] tw-text-amber-600 tw-text-4xl" width="40" height="40" />
                  </div>
                  <h3 className="tw-mb-12">Assessment Completed!</h3>
                </div>

                <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
                  <div className="tw-min-w-[0px] ui-column md:tw-w-5/6">
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                      <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Total Questions:</span>
                      <span className="tw-text-lg">{submissionResult.total_questions}</span>
                    </div>
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                      <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Attempted Questions:</span>
                      <span className="tw-text-lg">{attemptedCount}</span>
                    </div>
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                      <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Correct Answers:</span>
                      <span className="tw-text-lg">{correctCount}</span>
                    </div>
                    {isAdaptive && (
                      <>
                        <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                          <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Wrong Answers:</span>
                          <span className="tw-text-lg">{wrongCount}</span>
                        </div>
                        <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                          <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Unanswered Questions:</span>
                          <span className="tw-text-lg">{unansweredCount}</span>
                        </div>
                      </>
                    )}
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                      <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Your Score:</span>
                      <span className="tw-text-lg">{percentage}%</span>
                    </div>
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                      <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Pass Percentage:</span>
                      <span className="tw-text-lg">{passPercentage}%</span>
                    </div>
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4">
                      <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Status:</span>
                      <span className={`tw-text-lg   ${passed ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : "!tw-text-[var(--danger-color)] tw-text-rose-600"}`}>
                        {passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="tw-mt-12 tw-flex tw-justify-center">
                  <button
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-flex tw-items-center tw-justify-center tw-gap-2"}
                    style={{ minWidth: '200px' }}
                    onClick={() => navigate(classroomDetailsPath)}
                  >
                    <Icon icon="mdi:arrow-left" width="20" height="20" />
                    Back to Classroom
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  if (loadStatus === 'unavailable') {
    return (
      <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container tw-py-12">
        <ContentUnavailable
          title="This assessment is not available"
          description="This assessment is inactive or no longer available. Your saved answers remain on record."
          icon="mdi:clipboard-remove-outline"
          backLabel="Back to Classroom"
          backTo={classroomDetailsPath}
        />
      </div>
    );
  }

  if (isLoading || !assessmentData) {
    return (
      <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container tw-py-12">
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
          <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
            <div className="tw-text-center">
              <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
                <span className="tw-sr-only">Loading...</span>
              </div>
              <p className="tw-mt-4">Loading assessment...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdaptive && !assessmentData?.questions?.length) {
    return (
      <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container tw-py-12">
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
          <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
            <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-shadow-sm tw-border-0"}>
              <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                <NoData
                  message="No Assessment Questions Available"
                  description="The instructor hasn't added any questions to this assessment yet."
                  icon="mdi:file-document-outline"
                />
                <div className="tw-text-center tw-pb-6">
                  <button
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-flex tw-items-center tw-gap-2 tw-mx-auto"}
                    onClick={() => navigate(classroomDetailsPath)}
                  >
                    <Icon icon="mdi:arrow-left" width="20" height="20" />
                    Back to Classroom
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentNormalQuestion = !isAdaptive ? assessmentData.questions[currentQuestionIndex] : null;
  const adaptiveTotalQuestions = Number(adaptiveAttempt?.total_questions || assessmentData?.total_questions || 0);
  const normalIsLastQuestion =
    !isAdaptive && currentQuestionIndex === (assessmentData.questions.length - 1);

  return (
    <>
      {renderExitConfirmModal()}
      <div className="tw-w-full ui-container-fluid tw-py-6">
      <div className={"tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-flex tw-justify-center tw-items-center"}>
        <div className="tw-min-w-[0px] ui-column lg:tw-w-2/3 xl:tw-w-7/12" style={{ minWidth: 0 }}>
          <div className="tw-mb-4">
            <button
              type="button"
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-border-[1px] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-p-0 tw-m-0 tw-text-[rgb(64_84_214_/_var(--tw-text-opacity,_1))] [transition:opacity_0.2s_ease] tw-bg-[transparent] [--tw-text-opacity:1] [&:hover]:tw-opacity-[0.8] [&:hover]:tw-text-[#dc3545] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-button ui-button-link tw-no-underline tw-ps-0 tw-inline-flex tw-items-center tw-gap-1"}
              onClick={requestExitConfirmation}
              disabled={isSubmitting || isSavingAnswer}
            >
              <Icon icon="mdi:arrow-left" width="18" height="18" />
              Back
            </button>
          </div>
          <div className="tw-mb-6">
            <h4 className="tw-mb-0 tw-text-center">{assessmentData.assessment_name}</h4>
            {assessmentData?.activate_time && assessmentData?.deactivate_time && (
              <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-mb-0 tw-text-center tw-text-slate-500 tw-text-sm tw-mt-2">
                {`Window: ${formatUtcToLocal(assessmentData.activate_time)} - ${formatUtcToLocal(assessmentData.deactivate_time)}`}
              </p>
            )}
            {isAdaptive && (
              <p className="!tw-text-[var(--primary-color)] !tw-text-[12px] tw-mb-0 tw-text-center tw-text-brand-600 tw-text-sm tw-mt-2">
                Adaptive assessment mode is active for this quiz.
              </p>
            )}
          </div>

          <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-mb-6">
            <div className="tw-flex tw-items-center tw-mb-4 md:tw-mb-0">
              <div className="!tw-bg-[var(--primary-color)] tw-bg-brand-600 tw-rounded-full tw-text-white tw-p-2 tw-me-2 tw-flex tw-items-center tw-justify-center" style={{ width: '30px', height: '30px' }}>
                <small>{isAdaptive ? adaptiveQuestionNumber : currentQuestionIndex + 1}</small>
              </div>
              <span>
                Question {isAdaptive ? adaptiveQuestionNumber : currentQuestionIndex + 1} of{' '}
                {isAdaptive ? adaptiveTotalQuestions : assessmentData.total_questions}
              </span>
            </div>
            <div className="tw-flex tw-gap-2 tw-flex-wrap">
              <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] ![font-weight:var(--font-weight-medium)] !tw-text-[var(--primary-color)] !tw-text-[12px] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-rounded-full ui-badge tw-border tw-border-brand-600 tw-text-brand-600 tw-bg-brand-50 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-font-medium tw-text-sm"}>
                <Icon icon="mdi:help-circle" width="14" height="14" />
                {isAdaptive ? adaptiveTotalQuestions : assessmentData.total_questions} Questions
              </span>
              {isAdaptive && adaptiveQuestion?.question_level && (
                <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] ![font-weight:var(--font-weight-medium)] !tw-text-[var(--info-color)] !tw-text-[12px] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-rounded-full ui-badge tw-border tw-border-sky-600 tw-text-sky-600 tw-bg-sky-50 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-font-medium tw-text-sm"}>
                  <Icon icon="mdi:stairs-up" width="14" height="14" />
                  {adaptiveQuestion.question_level}
                </span>
              )}
              {timeLeft !== null ? (
                <span className={`![font-weight:var(--font-weight-medium)] !tw-text-[12px] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-rounded-full ui-badge tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-font-medium tw-text-sm   ${
                  timeLeft <= 60 ? "[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--danger-color)] tw-border tw-border-rose-600 tw-text-rose-600 tw-bg-rose-50" : "[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--info-color)] tw-border tw-border-sky-600 tw-text-sky-600 tw-bg-sky-50"
                }`}>
                  <Icon icon="mdi:clock-outline" width="14" height="14" />
                  {formatTime(timeLeft)}
                </span>
              ) : (
                <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] ![font-weight:var(--font-weight-medium)] !tw-text-[var(--success-color)] !tw-text-[12px] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-rounded-full ui-badge tw-border tw-border-emerald-600 tw-text-emerald-600 tw-bg-emerald-50 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-font-medium tw-text-sm"}>
                  <Icon icon="mdi:clock-outline" width="14" height="14" />
                  Untimed
                </span>
              )}
            </div>
          </div>

          <div className="tw-bg-[rgb(241_245_249_/_var(--tw-bg-opacity,_1))] tw-rounded-full tw-overflow-hidden tw-flex tw-h-2 [--tw-bg-opacity:1] ui-progress tw-mb-6" style={{ height: '4px' }}>
            <div
              className="tw-h-full tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] tw-rounded-full [transition:width_0.3s_ease] tw-flex tw-items-center tw-justify-center [--tw-bg-opacity:1] tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [transition-property:width] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:300ms] ui-progress-bar"
              role="progressbar"
              style={{ width: `${progress}%` }}
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>

          <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
            <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
              <h5 className="[overflow-wrap:break-word] [word-break:break-word] tw-text-[1rem] tw-font-semibold tw-text-[#2c3e50] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-leading-[1.5rem] ui-card-title tw-mb-6 assessment-quiz-question-title">
                {isAdaptive ? adaptiveQuestion?.question_name : currentNormalQuestion?.question_name}
              </h5>

              <div className="tw-flex tw-flex-col tw-gap-4">
                {(isAdaptive ? adaptiveQuestion?.options : currentNormalQuestion?.options)?.map((option) => (
                  <div
                    key={option.id}
                    role="button"
                    tabIndex={0}
                    className={`tw-w-full tw-min-w-[0] [&_.ui-check-input]:tw-shrink-[0] [&_.ui-check-input]:tw-mt-[0.2rem] [&_.ui-check-input]:tw-static [&_.ui-check-input]:[float:none] [&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-flex tw-items-center tw-gap-2 assessment-quiz-option ui-check tw-border tw-rounded-xl tw-p-4   ${selectedAnswer === option.id ? "tw-border-brand-600 tw-bg-slate-50" : ""}   !tw-cursor-pointer cursor-pointer`}
                    onClick={() => handleOptionSelect(option.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOptionSelect(option.id);
                      }
                    }}
                  >
                    <input
                      type="radio"
                      className={"[&:checked]:tw-bg-[#0d6efd] [&:checked]:tw-border-[#0d6efd] tw-cursor-pointer tw-w-4 tw-h-4 tw-shrink-[0] tw-rounded-[0.25rem] [--tw-border-opacity:1] tw-border-[rgb(203_213_225_/_var(--tw-border-opacity,_1))] ui-check-input"}
                      checked={selectedAnswer === option.id}
                      onChange={() => handleOptionSelect(option.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="[flex:1] tw-min-w-[0] [overflow-wrap:anywhere] [word-break:break-word] tw-whitespace-normal tw-leading-[1.45] assessment-quiz-option-text">
                      {option.option_value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-between tw-mt-6">
            {isAdaptive ? (
              <button
                className={"!tw-bg-[#6c757d] !tw-border-[#6c757d] !tw-text-[#fff] [&:hover:not(:disabled)]:!tw-bg-[#5c636a] [&:hover:not(:disabled)]:!tw-border-[#565e64] [&:hover:not(:disabled)]:!tw-text-[#fff] [&:focus-visible:not(:disabled)]:!tw-bg-[#5c636a] [&:focus-visible:not(:disabled)]:!tw-border-[#565e64] [&:focus-visible:not(:disabled)]:!tw-text-[#fff] [&:disabled]:!tw-border-[#6c757d] [&:disabled]:tw-opacity-[0.65] !tw-w-[120px] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] ui-button assessment-quiz-stop-btn w-120"}
                onClick={requestExitConfirmation}
                disabled={isSubmitting || isSavingAnswer}
              >
                Stop Assessment
              </button>
            ) : (
              <button
                className={"!tw-w-[120px] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [[data-theme='dark']_&]:tw-border-[#4895ef] [[data-theme='dark']_&]:tw-text-[#4895ef] [[data-theme='dark']_&:hover]:tw-bg-[#4895ef] [[data-theme='dark']_&:hover]:tw-text-[white] tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] tw-border-[rgb(199_210_254_/_var(--tw-border-opacity,_1))] tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-border-opacity:1] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [&:hover]:tw-text-[white] [&:hover]:tw-bg-[#4169E1] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] ui-button ui-button-outline-primary w-120"}
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </button>
            )}

            {isAdaptive ? (
              <button
                className={"!tw-w-[120px] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary w-120"}
                onClick={handleAdaptiveNext}
                disabled={!selectedAnswer || isSavingAnswer}
              >
                {isSavingAnswer ? (
                  <>
                    <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-1" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  adaptiveQuestionNumber >= adaptiveTotalQuestions ? 'Submit' : 'Next'
                )}
              </button>
            ) : normalIsLastQuestion ? (
              <button
                className={"!tw-w-[120px] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary w-120"}
                onClick={() => handleSubmit(false)}
                disabled={!selectedAnswer || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-1" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            ) : (
              <button
                className={"!tw-w-[120px] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary w-120"}
                onClick={handleNormalNext}
                disabled={!selectedAnswer || isSavingAnswer}
              >
                {isSavingAnswer ? (
                  <>
                    <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-1" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Next'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default AssessmentQuiz;
